/**
 * Marvel Commercial LED Uploader Page
 * 
 * Premium themed upload form for commercial/sponsor LED content.
 * Note: Marvel has 3 levels vs MCG's 4 levels.
 */

import React, { useState, useCallback, useRef } from "react";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useSessionExpiry } from "../../../hooks/useSessionExpiry";
import UserDetailsFields from "../../../components/uploaders/UserDetailsFields";
import { useVFContext } from "../../../context/VFContext";
import { useToast } from "../../../components/ui/UploaderToast";
import LedUploaderSection from "../../../components/uploaders/LedUploaderSection";
import UploadSuccessModal from "../../../components/modal/UploadSuccessModal";
import {
  LED_FORMATS,
  SPECS_BY_FORMAT,
  ALL_SPECS,
  METADATA_WEBHOOK,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
} from "../../../config/marvelCommercialLedSpecs";
import { generateSessionId, generateCrockfordTimeId } from "../../../services/idGenerator";

// ============================================================
// STYLES - Premium Theme
// ============================================================
const styles = {
  page: { minHeight: "100vh", height: "100%", backgroundColor: "#f7f7f5", fontFamily: "'Space Grotesk', -apple-system, sans-serif" },
  header: { backgroundColor: "#111114", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerTitle: { fontSize: 18, fontWeight: 600, color: "#fff", margin: 0 },
  headerSub: { fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 2 },
  headerBadges: { display: "flex", alignItems: "center", gap: 8 },
  venueBadge: { backgroundColor: "rgba(47, 214, 181, 0.15)", border: "1px solid rgba(47, 214, 181, 0.4)", color: "#2fd6b5", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600 },
  commercialBadge: { backgroundColor: "rgba(251, 191, 36, 0.15)", border: "1px solid rgba(251, 191, 36, 0.4)", color: "#fbbf24", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600 },
  accentLine: { height: 3, background: "linear-gradient(90deg, #2fd6b5, #84cc16)" },
  content: { padding: "32px 40px 48px", maxWidth: 1100, margin: "0 auto" },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 },
  typeSection: { marginBottom: 28 },
  typeRow: { display: "flex", flexWrap: "wrap", gap: 10 },
  typeBtn: { padding: "10px 18px", fontSize: 13, fontWeight: 500, color: "#555", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 8, cursor: "pointer", transition: "all 0.15s ease", fontFamily: "inherit" },
  typeBtnHover: { borderColor: "#bbb", backgroundColor: "#fafafa", color: "#333" },
  typeBtnActive: { backgroundColor: "#111114", borderColor: "#111114", color: "#fff", fontWeight: 600 },
  uploadSection: { backgroundColor: "#fff", borderRadius: 14, border: "2px solid #2fd6b5", boxShadow: "0 0 0 4px rgba(47, 214, 181, 0.1), 0 4px 20px rgba(0, 0, 0, 0.06)", overflow: "hidden", marginBottom: 28 },
  uploadHeader: { backgroundColor: "#111114", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  uploadTitle: { fontSize: 14, fontWeight: 600, color: "#fff", margin: 0, display: "flex", alignItems: "center", gap: 8 },
  uploadTitleIcon: { color: "#2fd6b5" },
  uploadBody: { padding: 24 },
  ledGroup: { marginBottom: 16 },
  ledHeading: { margin: "0 0 6px", fontSize: 16, fontWeight: 600, color: "#333" },
  ledHelp: { margin: "0 0 16px", fontSize: 13, color: "#666" },
  warningText: { color: "#dc2626", fontWeight: 600 },
  notesSection: { marginBottom: 32 },
  textarea: { width: "100%", padding: "14px 16px", fontSize: 14, color: "#333", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 10, resize: "vertical", minHeight: 80, fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s ease, box-shadow 0.15s ease" },
  textareaFocus: { borderColor: "#2fd6b5", boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)" },
  charCount: { fontSize: 11, color: "#999", textAlign: "right", marginTop: 6 },
  footer: { paddingTop: 24, borderTop: "1px solid #e5e5e3", display: "flex", justifyContent: "flex-end" },
  submitBtn: { padding: "12px 32px", fontSize: 15, fontWeight: 600, color: "#000", background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s ease" },
  submitBtnHover: { transform: "translateY(-1px)", boxShadow: "0 6px 16px rgba(47, 214, 181, 0.35)" },
  submitBtnDisabled: { opacity: 0.4, cursor: "not-allowed", transform: "none", boxShadow: "none" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 },
  field: {},
  label: { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, display: "block" },
  input: { width: "100%", padding: "11px 14px", fontSize: 14, color: "#333", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 8, fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s ease, box-shadow 0.15s ease" },
  inputFocus: { borderColor: "#2fd6b5", boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)" },
  placeholderBox: { backgroundColor: "#fafaf8", borderRadius: 10, border: "2px dashed #ddd", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, color: "#999", fontSize: 14, textAlign: "center", minHeight: 200 },
  assetNameRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  assetNameFieldWrap: { width: 340 },
  assetNameLabel: { fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, display: "block" },
  assetNameInput: { width: "100%", padding: "10px 14px", fontSize: 14, color: "#333", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 8, fontFamily: "inherit", outline: "none", boxSizing: "border-box", transition: "border-color 0.15s ease, box-shadow 0.15s ease" },
  assetNameInputFocus: { borderColor: "#2fd6b5", boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)" },
  assetNameInputLocked: { backgroundColor: "#f3f4f6", color: "#6b7280", borderColor: "#e5e7eb", cursor: "default" },
  assetNameBtn: { padding: "10px 18px", fontSize: 13, fontWeight: 600, color: "#000", background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.15s ease" },
  assetNameBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  assetNameEditBtn: { padding: "10px 18px", fontSize: 13, fontWeight: 600, color: "#555", backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.15s ease" },
  assetNameEditBtnWarning: { color: "#92400e", backgroundColor: "#fffbeb", borderColor: "#f59e0b" },
  assetNamePreview: { fontSize: 12, color: "#888", marginTop: 6, fontFamily: "'SF Mono', 'Fira Code', monospace" },
  uploadersDisabledOverlay: { opacity: 0.4, pointerEvents: "none", filter: "grayscale(0.3)" },
};

function TypeButton({ label, isActive, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button type="button" onClick={onClick} disabled={disabled} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ ...styles.typeBtn, ...(hovered && !isActive ? styles.typeBtnHover : {}), ...(isActive ? styles.typeBtnActive : {}), ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}>
      {label}
    </button>
  );
}

const MAX_DESC = 250;

const COLOUR_OPTIONS = [
  { id: "Red", hex: "#ef4444" },
  { id: "Blue", hex: "#3b82f6" },
  { id: "Green", hex: "#22c55e" },
  { id: "Yellow", hex: "#eab308" },
  { id: "Orange", hex: "#f97316" },
  { id: "Purple", hex: "#a855f7" },
  { id: "Pink", hex: "#ec4899" },
  { id: "Black", hex: "#111827" },
  { id: "White", hex: "#f9fafb" },
  { id: "Gold", hex: "#d4a017" },
  { id: "Silver", hex: "#9ca3af" },
  { id: "Brown", hex: "#92400e" },
  { id: "Multi", hex: null },
];

const INDUSTRY_OPTIONS = [
  { id: "Automotive" },
  { id: "Banking" },
  { id: "Insurance" },
  { id: "Superannuation" },
  { id: "Alcohol" },
  { id: "Beverages" },
  { id: "QSR & Food Delivery" },
  { id: "Supermarkets & Grocery" },
  { id: "Fashion & Apparel" },
  { id: "Hardware & Trade" },
  { id: "Home & Lifestyle" },
  { id: "Electronics & Technology" },
  { id: "Telecommunications" },
  { id: "Energy & Fuel" },
  { id: "Health & Pharmacy" },
  { id: "Gaming & Wagering" },
  { id: "Travel & Transport" },
  { id: "Entertainment & Media" },
  { id: "Government" },
  { id: "FMCG" },
  { id: "Professional Services" },
  { id: "Venue & Events" },
];


export function MarvelCommercialLEDPage() {
  const context = useVFContext();
  const { email: authEmail, name: authName } = useAuthUser();
  const { showError, ToastComponent } = useToast();

  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [submissionTimeId, setSubmissionTimeId] = useState(() => generateCrockfordTimeId());
  const { markFileUploaded, isExpired, resetExpiry, formatRemaining, urgency } = useSessionExpiry();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(authEmail || "");
  const [description, setDescription] = useState("");
  const [activeFormat, setActiveFormat] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [uploadResults, setUploadResults] = useState({});
  const [referenceDuration, setReferenceDuration] = useState(null);
  const activeUploads = useRef(0);

  // Asset Name state (for tiled/360 formats)
  const [assetName, setAssetName] = useState("");
  const [isAssetNameLocked, setIsAssetNameLocked] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Partner, Colour, Industry state (required for all formats)
  const [partner, setPartner] = useState("");
  const [selectedColour, setSelectedColour] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");

  // Round allocations state (for tiled/360 formats)



  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastUploadInfo, setLastUploadInfo] = useState(null);

  const activeSpecs = activeFormat ? SPECS_BY_FORMAT[activeFormat] : null;

  const ASSET_NAME_FORMATS = ["tiled", "360"];
  const needsAssetName = activeFormat && ASSET_NAME_FORMATS.includes(activeFormat);
  const needsColour = needsAssetName;
  const allFieldsSet = partner.trim() && selectedIndustry && (needsColour ? selectedColour : true);

  const handleFormatChange = useCallback((newFormat) => {
    if (activeFormat && activeFormat !== newFormat) { setUploadResults({}); setReferenceDuration(null); }
    setActiveFormat(newFormat);
    setAssetName("");
    setIsAssetNameLocked(false);
  }, [activeFormat]);

  const handleUploadComplete = useCallback(({ uploadId, file }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    if (activeUploads.current === 0) setIsUploading(false);
    markFileUploaded();
    setUploadResults((prev) => {
      const spec = ALL_SPECS[uploadId];
      return { ...prev, [uploadId]: { contentGroup: spec?.contentGroup, ledType: spec?.ledType, assets: [...(prev[uploadId]?.assets || []), file] } };
    });
  }, [markFileUploaded]);

  const handleUploadStart = useCallback(() => { activeUploads.current += 1; setIsUploading(true); }, []);
  const handleUploadCancel = useCallback(({ uploadId, fileId }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    if (activeUploads.current === 0) setIsUploading(false);
    setUploadResults((prev) => ({ ...prev, [uploadId]: { ...prev[uploadId], assets: prev[uploadId]?.assets?.filter((a) => a.id !== fileId) || [] } }));
  }, []);
  const handleDurationDetected = useCallback((duration) => { if (!referenceDuration) setReferenceDuration(duration); }, [referenceDuration]);

  const getIncompleteSections = useCallback(() => {
    if (!activeSpecs) return [];
    const errors = [];
    for (const [uploadId, spec] of Object.entries(activeSpecs)) {
      const uploaded = uploadResults[uploadId]?.assets?.length || 0;
      const required = spec.requiredFiles ?? spec.maxFiles ?? 1;
      if (uploaded !== required) errors.push(`${spec.label}: ${Math.max(required - uploaded, 0)} more file(s) required`);
    }
    return errors;
  }, [activeSpecs, uploadResults]);

  const resetForm = useCallback(() => {
    activeUploads.current = 0; setIsUploading(false); setUploadResults({}); setReferenceDuration(null);
    setSessionId(generateSessionId()); setSubmissionTimeId(generateCrockfordTimeId());
    setDescription(""); setActiveFormat(null);
    setAssetName(""); setIsAssetNameLocked(false);
    setPartner(""); setSelectedColour(""); setSelectedIndustry("");
    resetExpiry();
  }, [resetExpiry]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isExpired()) { showError("Session expired — uploaded files may have been cleaned up. Please start a new upload.", { duration: 12000 }); return; }
    if (!partner.trim()) { showError("Please enter a Partner name.", { duration: 9000 }); return; }
    if (needsColour && !selectedColour) { showError("Please select a Primary Colour.", { duration: 9000 }); return; }
    if (!selectedIndustry) { showError("Please select an Industry.", { duration: 9000 }); return; }
    if (!activeFormat) { showError("Please select an LED format.", { duration: 9000 }); return; }
    if (needsAssetName && !isAssetNameLocked) { showError("Please set an Asset Name before submitting.", { duration: 9000 }); return; }
    if (activeUploads.current > 0) { showError("Uploads are still in progress.", { duration: 12000 }); return; }
    const incomplete = getIncompleteSections();
    if (incomplete.length > 0) { showError(`Please complete:\n${incomplete.map((e) => `• ${e}`).join("\n")}`, { duration: 12000 }); return; }

    setIsSubmitting(true);
    try {
      const filteredContentUpload = Object.fromEntries(Object.entries(uploadResults).filter(([_, data]) => data?.assets?.length > 0 && data.ledType === activeFormat));
      const payload = {
        sessionId, submissionParent: context.submissionParent, completedAt: new Date().toISOString(),
        name: authName || name || context.submitterName, email: authEmail || email, description,
        venue: "Marvel", ledFormat: activeFormat, submissionType: "commercial",
        partner: partner.trim(),
        ...(needsColour && selectedColour ? { primaryColour: selectedColour } : {}),
        industry: selectedIndustry,
        ...(needsAssetName && assetName ? { assetName } : {}),
        ContentUpload: filteredContentUpload,
      };
      const res = await fetch(METADATA_WEBHOOK, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(`Submission failed (${res.status})`);
      
      // LED uses shared ID
      const clubCode = context.submitterShortName || "VF";
      const shortContentId = `${clubCode}_${submissionTimeId}`;
      const formatLabel = formatLabels[activeFormat]?.heading || activeFormat;

      // Auto-seed into club LED playlists (non-blocking)
      
      setLastUploadInfo({
        assets: [{ contentId: shortContentId, fileName: `${formatLabel} (${uploadCount} files)` }],
        destination: `Marvel Commercial LED - ${formatLabel}`,
      });
      setShowSuccessModal(true);
      
      resetForm();
    } catch (err) {
      console.error("Submission error:", err);
      showError("Something went wrong. Please try again.", { duration: 6000 });
    } finally { setIsSubmitting(false); }
  };

  const uploadCount = Object.values(uploadResults).reduce((sum, r) => sum + (r?.assets?.length || 0), 0);
  const formatLabels = {
    tiled: { heading: "Tiled LED", help: "All three levels are required", warning: true },
    "360": { heading: "360 LED", help: "All three levels are required", warning: true },
    interchange: { heading: "Interchange LED", help: "One asset used across all levels.", warning: false },
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Commercial LED Assets</h1>
          <p style={styles.headerSub}>Upload sponsor/commercial LED content for Marvel Stadium</p>
        </div>
        <div style={styles.headerBadges}>
          <span style={styles.venueBadge}>Marvel Stadium</span>
          <span style={styles.commercialBadge}>Commercial</span>
        </div>
      </header>
      <div style={styles.accentLine} />

      <main style={styles.content}>
        <form onSubmit={handleSubmit}>
          <UserDetailsFields name={name} setName={setName} email={email} setEmail={setEmail} focusedField={focusedField} setFocusedField={setFocusedField} styles={styles} />

          <div style={styles.typeSection}>
            <div style={styles.sectionLabel}>LED Format</div>
            <div style={styles.typeRow}>
              {LED_FORMATS.map((f) => <TypeButton key={f.id} label={f.label} isActive={activeFormat === f.id} onClick={() => handleFormatChange(f.id)} disabled={isSubmitting} />)}
            </div>
          </div>

          <div style={styles.uploadSection}>
            <div style={styles.uploadHeader}>
              <h2 style={styles.uploadTitle}>
                <svg style={styles.uploadTitleIcon} width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v9M5 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 13v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Files
              </h2>
              {uploadCount > 0 && <span style={{ backgroundColor: "#2fd6b5", color: "#000", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>{uploadCount} file{uploadCount !== 1 ? "s" : ""} ready</span>}
            </div>
            <div style={styles.uploadBody}>
              {activeFormat && activeSpecs ? (
                <div style={styles.ledGroup}>
                  <h3 style={styles.ledHeading}>{formatLabels[activeFormat]?.heading}</h3>
                  <p style={styles.ledHelp}>{formatLabels[activeFormat]?.help}{formatLabels[activeFormat]?.warning && <> — <span style={styles.warningText}>All clip durations must be identical</span></>}</p>

                  {/* ── Commercial Details (format-specific) ── */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "grid", gridTemplateColumns: needsColour ? "1fr 1fr 1fr" : "1fr 1fr", gap: 16 }}>
                      {/* Partner */}
                      <div>
                        <label style={styles.label}>Partner <span style={{ color: "#dc2626" }}>*</span></label>
                        <input
                          type="text"
                          value={partner}
                          onChange={(e) => setPartner(e.target.value)}
                          disabled={isSubmitting || uploadCount > 0}
                          placeholder="Partner Name"
                          onFocus={() => setFocusedField("partner")}
                          onBlur={() => setFocusedField(null)}
                          style={{ ...styles.input, ...(focusedField === "partner" ? styles.inputFocus : {}), ...(uploadCount > 0 ? { backgroundColor: "#f3f4f6", opacity: 0.7 } : {}) }}
                        />
                      </div>

                      {/* Primary Colour — tiled/360 only */}
                      {needsColour && (
                        <div>
                          <label style={styles.label}>Primary Colour <span style={{ color: "#dc2626" }}>*</span></label>
                          <div style={{ position: "relative" }}>
                            <select
                              value={selectedColour}
                              onChange={(e) => setSelectedColour(e.target.value)}
                              disabled={isSubmitting || uploadCount > 0}
                              onFocus={() => setFocusedField("colour")}
                              onBlur={() => setFocusedField(null)}
                              style={{ ...styles.input, appearance: "none", paddingRight: 32, cursor: "pointer", ...(focusedField === "colour" ? styles.inputFocus : {}), ...(uploadCount > 0 ? { backgroundColor: "#f3f4f6", opacity: 0.7 } : {}) }}
                            >
                              <option value="">Select colour...</option>
                              {COLOUR_OPTIONS.map((c) => (
                                <option key={c.id} value={c.id}>{c.id}</option>
                              ))}
                            </select>
                            {selectedColour && (() => {
                              const colourObj = COLOUR_OPTIONS.find(c => c.id === selectedColour);
                              return colourObj ? (
                                <span style={{
                                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                                  width: 14, height: 14, borderRadius: "50%", pointerEvents: "none",
                                  ...(colourObj.hex
                                    ? { backgroundColor: colourObj.hex, border: colourObj.id === "White" ? "1px solid #ddd" : "1px solid transparent" }
                                    : { background: "conic-gradient(#ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)" }),
                                }} />
                              ) : null;
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Industry */}
                      <div>
                        <label style={styles.label}>Industry <span style={{ color: "#dc2626" }}>*</span></label>
                        <select
                          value={selectedIndustry}
                          onChange={(e) => setSelectedIndustry(e.target.value)}
                          disabled={isSubmitting || uploadCount > 0}
                          onFocus={() => setFocusedField("industry")}
                          onBlur={() => setFocusedField(null)}
                          style={{ ...styles.input, appearance: "none", cursor: "pointer", ...(focusedField === "industry" ? styles.inputFocus : {}), ...(uploadCount > 0 ? { backgroundColor: "#f3f4f6", opacity: 0.7 } : {}) }}
                        >
                          <option value="">Select industry...</option>
                          {INDUSTRY_OPTIONS.map((i) => (
                            <option key={i.id} value={i.id}>{i.id}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* ── Round Allocations (tiled/360 only) ── */}

                  {/* Asset Name field for Tiled and 360 formats */}
                  {needsAssetName && (
                    <div style={{ marginBottom: 8 }}>
                      <label style={styles.assetNameLabel}>Asset Name <span style={{ color: "#dc2626" }}>*</span></label>
                      <div style={styles.assetNameRow}>
                        <div style={styles.assetNameFieldWrap}>
                          <input
                            type="text"
                            value={assetName}
                            onChange={(e) => {
                              const sanitised = e.target.value.replace(/[^a-zA-Z0-9 _-]/g, "");
                              setAssetName(sanitised.slice(0, 30));
                            }}
                            disabled={isAssetNameLocked || isSubmitting}
                            placeholder="Lumen8 Asset"
                            maxLength={30}
                            onFocus={() => setFocusedField("assetName")}
                            onBlur={() => setFocusedField(null)}
                            style={{
                              ...styles.assetNameInput,
                              ...(focusedField === "assetName" && !isAssetNameLocked ? styles.assetNameInputFocus : {}),
                              ...(isAssetNameLocked ? styles.assetNameInputLocked : {}),
                            }}
                          />
                        </div>
                        {!isAssetNameLocked ? (
                          <button
                            type="button"
                            disabled={!assetName.trim() || isSubmitting}
                            onClick={() => {
                              setAssetName((prev) => prev.replace(/\s+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, ""));
                              setIsAssetNameLocked(true);
                            }}
                            style={{
                              ...styles.assetNameBtn,
                              ...(!assetName.trim() ? styles.assetNameBtnDisabled : {}),
                            }}
                          >
                            Set Name
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={isSubmitting || isUploading}
                            onClick={() => {
                              if (uploadCount > 0) {
                                if (!window.confirm("Changing the Asset Name after uploading files will reset the form and start a new session. All uploaded files will be lost.\n\nDo you want to continue?")) return;
                                resetForm();
                                return;
                              }
                              setAssetName("");
                              setIsAssetNameLocked(false);
                            }}
                            title={isUploading ? "Upload in progress \u2014 please wait" : uploadCount > 0 ? "Warning: this will reset the form and start a new session" : ""}
                            style={{
                              ...styles.assetNameEditBtn,
                              ...(isUploading ? { opacity: 0.4, cursor: "not-allowed" } : uploadCount > 0 ? styles.assetNameEditBtnWarning : {}),
                            }}
                          >
                            {uploadCount > 0 && !isUploading ? "Edit \u26a0" : "Edit"}
                          </button>
                        )}
                      </div>
                      {!isAssetNameLocked && (
                        <div style={{ fontSize: 11, color: "#999", textAlign: "right", marginTop: 4, width: 340 }}>{assetName.length}/30</div>
                      )}
                      {isAssetNameLocked && (
                        <div style={styles.assetNamePreview}>
                          Folder: {context.submitterShortName || "VF"}_{submissionTimeId}_{assetName}_Marvel-{activeFormat}
                        </div>
                      )}
                      {!isAssetNameLocked && (
                        <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                          Set the asset name to enable uploaders. Letters, numbers, hyphens and underscores only — spaces will be converted to underscores.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Uploaders — disabled until fields set and asset name set (for formats that require it) */}
                  {(() => {
                    const needsFields = !allFieldsSet;
                    const needsAssetNameSet = needsAssetName && !isAssetNameLocked;
                    const isGated = needsFields || needsAssetNameSet;
                    const gateMessage = needsFields
                      ? (needsColour ? "Complete Partner, Colour and Industry to enable uploads" : "Complete Partner and Industry to enable uploads")
                      : "Set an Asset Name above to enable uploads";
                    return (
                      <div style={isGated ? styles.uploadersDisabledOverlay : {}}>
                        {isGated && (
                          <div style={{ textAlign: "center", padding: "16px 0", fontSize: 13, color: "#999", fontWeight: 500 }}>
                            {gateMessage}
                          </div>
                        )}
                        {Object.entries(activeSpecs).map(([uploadId, spec]) => (
                          <LedUploaderSection key={`${sessionId}-${uploadId}`} uploadId={uploadId} spec={spec} context={context} sessionId={sessionId} submissionTimeId={submissionTimeId}
                            onUploadComplete={handleUploadComplete} onUploadStart={handleUploadStart} onUploadCancel={handleUploadCancel}
                            referenceDuration={referenceDuration} onDurationDetected={handleDurationDetected}
                            validateFilename={validateFilename} getDimensionsForFile={getDimensionsForFile} buildSpecLabel={buildSpecLabel}
                            venuePrefix="Marvel"
                            assetName={needsAssetName && isAssetNameLocked ? assetName : null} />
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div style={styles.placeholderBox}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12, color: "#ccc" }}><path d="M20 8v16M12 16l8-8 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 28v4a2 2 0 002 2h24a2 2 0 002-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  <p>Select an LED format above to start uploading</p>
                </div>
              )}
            </div>
          </div>

          <div style={styles.notesSection}>
            <div style={styles.sectionLabel}>Notes (Optional)</div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))} onFocus={() => setFocusedField("notes")} onBlur={() => setFocusedField(null)} placeholder="Add any notes for the operations team..." style={{ ...styles.textarea, ...(focusedField === "notes" ? styles.textareaFocus : {}) }} />
            <div style={styles.charCount}>{description.length}/{MAX_DESC}</div>
          </div>

          <div style={styles.footer}>
            {urgency && urgency !== "ok" && (
              <div style={{
                padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, marginBottom: 12,
                ...(urgency === "expired"
                  ? { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" }
                  : urgency === "critical"
                  ? { background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e" }
                  : { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" }),
              }}>
                {urgency === "expired"
                  ? "⛔ Session expired — uploaded files may have been removed. Please start a new upload."
                  : `⏳ Session expires in ${formatRemaining()} — please submit before files are cleaned up.`}
              </div>
            )}
            <button type="submit" disabled={isSubmitting || !allFieldsSet || !activeFormat || uploadCount === 0 || urgency === "expired"} onMouseEnter={() => setIsSubmitHovered(true)} onMouseLeave={() => setIsSubmitHovered(false)}
              style={{ ...styles.submitBtn, ...(isSubmitHovered && !isSubmitting && allFieldsSet && activeFormat && uploadCount > 0 && urgency !== "expired" ? styles.submitBtnHover : {}), ...((isSubmitting || !allFieldsSet || !activeFormat || uploadCount === 0 || urgency === "expired") ? styles.submitBtnDisabled : {}) }}>
              {isSubmitting ? "Submitting..." : "Submit"}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </form>
      </main>
      {ToastComponent}
      
      {/* Upload Success Modal */}
      <UploadSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        assets={lastUploadInfo?.assets || []}
        destination={lastUploadInfo?.destination}
      />
    </div>
  );
}

export default MarvelCommercialLEDPage;
